import PropTypes from "prop-types";
import { useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useColorScheme, useTheme } from "@mui/material/styles";

import useZebraRowColors from "hooks/useZebraRowColors";
import { parseAppDateTime } from "utils/dosingFormat";
import { surfaceBorderColor, surfaceTableFrameSx, surfaceTableSx } from "utils/surfaceTheme";
import { withAlpha } from "utils/colorUtils";

const TANK_COL_MIN_WIDTH = 132;
const TABLE_CELL_PX = 0.75;
const HEADER_ROW_MIN_HEIGHT = 36;
const DATA_ROW_MIN_HEIGHT = 88;

function sortTanksByLatestResult(tanks, latestByTankAndTest) {
  return [...tanks]
    .map((tank) => {
      const prefix = `${String(tank.id)}::`;
      let maxTs = Number.NEGATIVE_INFINITY;
      let hasData = false;

      latestByTankAndTest.forEach((row, key) => {
        if (!key.startsWith(prefix)) return;
        if (!row?.recorded_at) return;
        const ts = parseAppDateTime(row.recorded_at)?.getTime() ?? NaN;
        if (!Number.isFinite(ts)) return;
        hasData = true;
        maxTs = Math.max(maxTs, ts);
      });

      return { tank, sortTs: hasData ? maxTs : Number.NEGATIVE_INFINITY };
    })
    .sort((a, b) => b.sortTs - a.sortTs)
    .map((entry) => entry.tank);
}

function formatTargetAnnotation(test, goldenStandardValues) {
  const val = goldenStandardValues[test.code];
  if (!Number.isFinite(val)) return null;
  return `${val} ${test.primaryUnit || ""}`.trim();
}

function formatDateOnly(value) {
  if (!value) return "—";
  const d = parseAppDateTime(value);
  if (!d) return "—";
  return d.toLocaleDateString();
}

export default function SalifertTrendsDataMobileTable({
  tanks,
  visibleTests,
  latestByTankAndTest,
  allTargetsByTankCode,
  goldenStandardValues,
  legendSymbols,
  evaluateStatus,
}) {
  const theme = useTheme();
  const { mode: colorSchemeMode } = useColorScheme();
  const { tableBodyCellSx } = useZebraRowColors();
  const tableBorderColor = surfaceBorderColor(theme, colorSchemeMode, "table");

  const sortedTanks = useMemo(
    () => sortTanksByLatestResult(tanks, latestByTankAndTest),
    [tanks, latestByTankAndTest],
  );

  const cellPadding = theme.spacing(TABLE_CELL_PX);

  const columnDividerSx = {
    backgroundImage: `linear-gradient(${tableBorderColor}, ${tableBorderColor})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "1px calc(100% - 30px)",
    backgroundPosition: "right 16px",
  };

  const columnHeaderCellSx = {
    bgcolor: "transparent",
    color: "text.primary",
    whiteSpace: "nowrap",
    py: 0.75,
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    borderBottom: 2,
    borderBottomColor: tableBorderColor,
    minHeight: HEADER_ROW_MIN_HEIGHT,
    boxSizing: "border-box",
  };

  const tankTableSx = useMemo(
    () =>
      surfaceTableSx(theme, colorSchemeMode, {
        minWidth: "max-content",
        ...tableBodyCellSx,
        "& .MuiTableRow-root .MuiTableCell-root": {
          pl: `${cellPadding} !important`,
          pr: `${cellPadding} !important`,
        },
      }),
    [theme, colorSchemeMode, tableBodyCellSx, cellPadding],
  );

  const stickyTestsHeaderSx = {
    ...columnHeaderCellSx,
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 4,
    width: "max-content",
  };

  const stickyTestsLabelSx = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    bgcolor: "background.paper",
    width: "max-content",
    whiteSpace: "nowrap",
    verticalAlign: "top",
    height: DATA_ROW_MIN_HEIGHT,
    py: 0.5,
    boxSizing: "border-box",
    ...columnDividerSx,
  };

  const stickyTankHeaderSx = {
    ...columnHeaderCellSx,
    position: "sticky",
    top: 0,
    zIndex: 2,
    minWidth: TANK_COL_MIN_WIDTH,
  };

  if (tanks.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          ...surfaceTableFrameSx(theme, colorSchemeMode),
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No tanks or test data available yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
        bgcolor: "background.paper",
        ...surfaceTableFrameSx(theme, colorSchemeMode),
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        <Table size="small" stickyHeader sx={tankTableSx}>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={stickyTestsHeaderSx}>
                Tests
              </TableCell>
              {sortedTanks.map((tank) => (
                <TableCell
                  key={tank.id}
                  align="left"
                  sx={{
                    ...stickyTankHeaderSx,
                    "&:last-of-type": { backgroundImage: "none" },
                  }}
                >
                  {tank.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTests.map((test, index) => {
              const targetAnnotation = formatTargetAnnotation(
                test,
                goldenStandardValues,
              );
              const annotation =
                legendSymbols[test.code] ||
                String(test.code || "").toUpperCase();

              return (
                <TableRow key={test.code} hover>
                  <TableCell sx={stickyTestsLabelSx}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="baseline"
                      sx={{ minWidth: 0 }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          flexShrink: 0,
                          fontSize: "0.625rem",
                          lineHeight: 1.1,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          lineHeight: 1.1,
                        }}
                      >
                        {annotation}
                      </Typography>
                    </Stack>
                    {targetAnnotation ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.625rem",
                          lineHeight: 1.15,
                          mt: 0.15,
                          display: "block",
                        }}
                      >
                        {targetAnnotation}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {sortedTanks.map((tank) => {
                    const tankKey = String(tank.id);
                    const latest = latestByTankAndTest.get(
                      `${tankKey}::${test.code}`,
                    );
                    const current = latest
                      ? Number(latest.primary_value)
                      : NaN;
                    const target = allTargetsByTankCode.get(
                      `${tankKey}::${test.code}`,
                    );
                    const desired = Number.isFinite(target)
                      ? target
                      : goldenStandardValues[test.code];
                    const status = evaluateStatus(
                      current,
                      desired,
                      test.code,
                    );

                    return (
                      <TableCell
                        key={`${tank.id}-${test.code}`}
                        sx={{
                          minWidth: TANK_COL_MIN_WIDTH,
                          height: DATA_ROW_MIN_HEIGHT,
                          py: 0.5,
                          verticalAlign: "top",
                          boxSizing: "border-box",
                          ...columnDividerSx,
                          "&:last-of-type": { backgroundImage: "none" },
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.65,
                            minHeight: DATA_ROW_MIN_HEIGHT - 16,
                            boxSizing: "border-box",
                            bgcolor: status.bg,
                            border: `1px solid ${tableBorderColor}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.25 }}
                          >
                            {latest
                              ? `${latest.primary_value} ${latest.primary_unit || test.primaryUnit || ""}`.trim()
                              : "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.2 }}
                          >
                            Added:{" "}
                            {latest
                              ? formatDateOnly(latest.recorded_at)
                              : "—"}
                            {latest?._matchType === "next" ? " (next)" : ""}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.2 }}
                          >
                            {Number.isFinite(target) ? "Target" : "Desired"}:{" "}
                            {Number.isFinite(desired)
                              ? `${desired} ${test.primaryUnit || ""}`.trim()
                              : "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", lineHeight: 1.2 }}
                          >
                            Status: {status.label}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

SalifertTrendsDataMobileTable.propTypes = {
  tanks: PropTypes.arrayOf(PropTypes.object).isRequired,
  visibleTests: PropTypes.arrayOf(PropTypes.object).isRequired,
  latestByTankAndTest: PropTypes.instanceOf(Map).isRequired,
  allTargetsByTankCode: PropTypes.instanceOf(Map).isRequired,
  goldenStandardValues: PropTypes.object.isRequired,
  legendSymbols: PropTypes.object.isRequired,
  evaluateStatus: PropTypes.func.isRequired,
};
